import type { BoqRow, SimilarityBreakdown } from '../../types'
import { normalizeItemCode, normalizeText, normalizeUnit } from '../normalization'

function levenshteinSimilarity(left: string, right: string): number {
  if (left === right) return left ? 1 : 0
  if (!left || !right) return 0
  const leftCharacters = Array.from(left)
  const rightCharacters = Array.from(right)
  if (leftCharacters.length * rightCharacters.length > 250_000) return 0

  let previous = Array.from({ length: rightCharacters.length + 1 }, (_, index) => index)
  for (let leftIndex = 0; leftIndex < leftCharacters.length; leftIndex += 1) {
    const current = [leftIndex + 1]
    for (let rightIndex = 0; rightIndex < rightCharacters.length; rightIndex += 1) {
      const substitutionCost = leftCharacters[leftIndex] === rightCharacters[rightIndex] ? 0 : 1
      current.push(
        Math.min(
          (current[rightIndex] ?? 0) + 1,
          (previous[rightIndex + 1] ?? 0) + 1,
          (previous[rightIndex] ?? 0) + substitutionCost,
        ),
      )
    }
    previous = current
  }
  const distance = previous[rightCharacters.length] ?? Math.max(left.length, right.length)
  return 1 - distance / Math.max(leftCharacters.length, rightCharacters.length)
}

function bigrams(text: string): readonly string[] {
  const characters = Array.from(text)
  if (characters.length < 2) return characters
  const result: string[] = []
  for (let index = 0; index < characters.length - 1; index += 1) {
    result.push(`${characters[index]}${characters[index + 1]}`)
  }
  return result
}

function diceSimilarity(left: string, right: string): number {
  if (left === right) return left ? 1 : 0
  if (!left || !right) return 0
  const leftCounts = new Map<string, number>()
  for (const gram of bigrams(left)) leftCounts.set(gram, (leftCounts.get(gram) ?? 0) + 1)
  let intersection = 0
  const rightGrams = bigrams(right)
  for (const gram of rightGrams) {
    const count = leftCounts.get(gram) ?? 0
    if (count > 0) {
      intersection += 1
      leftCounts.set(gram, count - 1)
    }
  }
  return (2 * intersection) / (bigrams(left).length + rightGrams.length)
}

export function stringSimilarity(leftValue: unknown, rightValue: unknown): number {
  const left = normalizeText(leftValue)
  const right = normalizeText(rightValue)
  if (!left || !right) return 0
  return Math.max(levenshteinSimilarity(left, right), diceSimilarity(left, right))
}

interface WeightedField {
  key: keyof Omit<SimilarityBreakdown, 'weightedScore'>
  left: string
  right: string
  weight: number
  similarity: number
}

/** BOQ identity similarity: name 30%, feature 45%, unit 20%, code 5%. */
export function scoreRowSimilarity(baselineRow: BoqRow, targetRow: BoqRow): SimilarityBreakdown {
  const baselineCode = normalizeItemCode(baselineRow.itemCode)
  const targetCode = normalizeItemCode(targetRow.itemCode)
  const baselineName = normalizeText(baselineRow.itemName)
  const targetName = normalizeText(targetRow.itemName)
  const baselineDescription = normalizeText(baselineRow.itemDescription)
  const targetDescription = normalizeText(targetRow.itemDescription)
  const baselineUnit = normalizeUnit(baselineRow.unit)
  const targetUnit = normalizeUnit(targetRow.unit)
  const fields: WeightedField[] = [
    {
      key: 'itemName',
      left: baselineName,
      right: targetName,
      weight: 0.3,
      similarity: stringSimilarity(baselineName, targetName),
    },
    {
      key: 'itemDescription',
      left: baselineDescription,
      right: targetDescription,
      weight: 0.45,
      similarity: stringSimilarity(baselineDescription, targetDescription),
    },
    {
      key: 'unit',
      left: baselineUnit,
      right: targetUnit,
      weight: 0.2,
      similarity: baselineUnit && baselineUnit === targetUnit ? 1 : 0,
    },
    {
      key: 'itemCode',
      left: baselineCode,
      right: targetCode,
      weight: 0.05,
      similarity: stringSimilarity(baselineCode, targetCode),
    },
  ]

  const activeFields = fields.filter((field) => field.left || field.right)
  const activeWeight = activeFields.reduce((sum, field) => sum + field.weight, 0)
  const scores = {
    itemName: fields[0]?.similarity ?? 0,
    itemDescription: fields[1]?.similarity ?? 0,
    unit: fields[2]?.similarity ?? 0,
    itemCode: fields[3]?.similarity ?? 0,
  }
  const weightedScore =
    activeWeight === 0
      ? 0
      : activeFields.reduce((sum, field) => sum + field.similarity * field.weight, 0) / activeWeight

  return { ...scores, weightedScore }
}
